import React, { useState, useEffect } from "react";
//import { MapContainer, TileLayer, GeoJSON, Popup } from "react-leaflet";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import L from "leaflet";
import type { LatLngExpression } from "leaflet";

const ARCGIS_URL = process.env.REACT_APP_ARCGIS_URL || "https://mapas.terrenos.pr.gov/server/rest/services/InventariosTerrenos/ADM_Terrenos/FeatureServer/0";

const statusColor: Record<string, string> = {
  "0": "#22c55e",   // Disponible
  "1": "#3b82f6",   // Arrendada
  "2": "#ef4444",   // Vendida
  "3": "#f59e0b",   // En Proceso
};

const statusLabel: Record<string, string> = {
  "0": "Disponible",
  "1": "Arrendada",
  "2": "Vendida",
  "3": "En Proceso",
};

interface ArcGISFeature {
  type: string;
  id: number;
  geometry: {
    type: string;
    coordinates: number[][][];
  };
  properties: {
    OBJECTID: number;
    IDFINCA: string;
    FINCA: string;
    MUNICIPIO: string;
    DISPONIBILIDAD: number | null;
    NUM_FINCA: string;
    ADQUIRIDA_DE_NOMBRE: string;
    ARRENDADO_A: string;
    CABIDA_ARRENDADA_CDAS: number | null;
    CABIDA_ESCRITURA_CDAS: number | null;
    CUERDAS_MAPA: number | null;
    NOMBRE_PLANO: string;
    NUMERO_CATASTRO: string;
    Shape__Area: number;
    [key: string]: string | number | null | undefined;
  };
}

interface GeoJSONData {
  type: string;
  features: ArcGISFeature[];
}

interface Props {
  fincaId: string;
  height?: string;
}

export const ArcGISParcelMap: React.FC<Props> = ({ fincaId, height = "500px" }) => {
  const [geoData, setGeoData] = useState<GeoJSONData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<LatLngExpression>([18.22, -66.59]);
  const [mapZoom, setMapZoom] = useState(9);

  useEffect(() => {
    if (!fincaId) return;

    const fetchParcelGeometry = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `${ARCGIS_URL}/query?where=FINCA%3D%27${fincaId}%27&outFields=IDFINCA,FINCA,MUNICIPIO,DISPONIBILIDAD,NUM_FINCA,ADQUIRIDA_DE_NOMBRE,ARRENDADO_A,CABIDA_ARRENDADA_CDAS,CABIDA_ESCRITURA_CDAS,CUERDAS_MAPA,NOMBRE_PLANO,NUMERO_CATASTRO&outSR=4326&f=geojson`;

        const resp = await fetch(url);
        const data = await resp.json();

        if (data.error) {
          setError(data.error.message || "Error al consultar ArcGIS");
          return;
        }

        if (data.features && data.features.length > 0) {
          setGeoData(data as GeoJSONData);

          // Calculate center from the first feature's bounds
          const layer = L.geoJSON(data);
          const bounds = layer.getBounds();
          const center = bounds.getCenter();
          setMapCenter([center.lat, center.lng]);
          setMapZoom(17); // Zoom in close to parcel level
        } else {
          setError("No se encontraron parcelas en el mapa para esta finca");
        }
      } catch (err) {
        console.error("ArcGIS fetch error:", err);
        setError("Error de conexión con el servicio de mapas");
      } finally {
        setLoading(false);
      }
    };

    fetchParcelGeometry();
  }, [fincaId]);

  const styleFeature = (feature: ArcGISFeature | undefined) => {
    const disp = String(feature?.properties?.DISPONIBILIDAD ?? "0");
    return {
      color: statusColor[disp] ?? "#6b7280",
      weight: 2,
      opacity: 0.9,
      fillColor: statusColor[disp] ?? "#6b7280",
      fillOpacity: 0.25,
    };
  };

  const onEachFeature = (feature: ArcGISFeature, layer: L.Layer) => {
    const p = feature.properties;
    const disp = String(p.DISPONIBILIDAD ?? "0");
    const cabida = p.CUERDAS_MAPA || p.CABIDA_ARRENDADA_CDAS || p.CABIDA_ESCRITURA_CDAS;

    const popup = `
      <div style="font-family: Segoe UI, sans-serif; font-size: 13px; min-width: 200px;">
        <div style="font-weight: 700; font-size: 14px; color: #111; margin-bottom: 4px;">${p.IDFINCA}</div>
        <div style="color: #666; margin-bottom: 8px;">${p.MUNICIPIO}</div>
        <div style="display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; color: white; background: ${statusColor[disp] ?? "#6b7280"}; margin-bottom: 8px;">
          ${statusLabel[disp] ?? "—"}
        </div>
        <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
          ${p.ARRENDADO_A ? `<tr><td style="color: #999; padding: 2px 0;">Arrendado a</td><td style="text-align: right; font-weight: 500;">${p.ARRENDADO_A}</td></tr>` : ""}
          ${cabida ? `<tr><td style="color: #999; padding: 2px 0;">Cabida</td><td style="text-align: right; font-weight: 500;">${cabida.toFixed(2)} cdas</td></tr>` : ""}
          ${p.NUMERO_CATASTRO ? `<tr><td style="color: #999; padding: 2px 0;">Catastro</td><td style="text-align: right; font-weight: 500;">${p.NUMERO_CATASTRO}</td></tr>` : ""}
          ${p.NOMBRE_PLANO ? `<tr><td style="color: #999; padding: 2px 0;">Plano</td><td style="text-align: right; font-weight: 500;">${p.NOMBRE_PLANO}</td></tr>` : ""}
        </table>
      </div>
    `;

    (layer as L.Path).bindPopup(popup);
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 shadow-sm flex items-center justify-center bg-gray-50" style={{ height }}>
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full mx-auto mb-3"></div>
          <span className="text-sm text-gray-400">Cargando mapa de parcela...</span>
        </div>
      </div>
    );
  }

  if (error || !geoData) {
    return (
      <div className="rounded-xl border border-gray-200 shadow-sm flex items-center justify-center bg-gray-50" style={{ height }}>
        <div className="text-center px-6">
          <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          <span className="text-sm text-gray-400">{error || "No hay datos de mapa disponibles"}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200" style={{ height }}>
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={true}
      >
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        />
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
        />
        <GeoJSON
          key={fincaId}
          data={geoData as GeoJSON.FeatureCollection}
          style={(feature) => styleFeature(feature as unknown as ArcGISFeature)}
          onEachFeature={(feature, layer) => onEachFeature(feature as unknown as ArcGISFeature, layer)}
        />
      </MapContainer>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-2 bg-white border-t text-xs text-gray-500">
        <span className="font-medium text-gray-700">Finca {fincaId}</span>
        <span className="text-gray-300">|</span>
        <span>{geoData.features.length} sub-parcela{geoData.features.length !== 1 ? "s" : ""}</span>
        <span className="text-gray-300">|</span>
        {Object.entries(statusColor).map(([key, color]) => (
          <div key={key} className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-sm" style={{ background: color, opacity: 0.7 }} />
            <span>{statusLabel[key]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
